import React, { useEffect, useState, useCallback } from "react";
import "./App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from './context/AuthContext';
import { SubscriptionProvider } from './context/SubscriptionContext';
import { OfflineProvider } from './context/OfflineContext';
import { Toaster } from './components/ui/toaster';
import { checkVersion } from './utils/versionCheck';
import RenewalModal from './components/RenewalModal';
import OfflineBanner from './components/OfflineBanner';
import SplashScreen from './components/SplashScreen';
import LandingPage from './pages/LandingPage';
import Login from './pages/Login';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ChoosePlan from './pages/ChoosePlan';
import Payment from './pages/Payment';
import PaymentSuccess from './pages/PaymentSuccess';
import PaymentFailure from './pages/PaymentFailure';
import SubscriptionPending from './pages/SubscriptionPending';
import SubscriptionExpired from './pages/SubscriptionExpired';
import SubscriptionRejected from './pages/SubscriptionRejected';
import PrivacyPolicy from './pages/PrivacyPolicy';
import ContactUs from './pages/ContactUs';
import TermsAndConditions from './pages/TermsAndConditions';
import Dashboard from './pages/Dashboard';
import MasterDashboard from './pages/MasterDashboard';
import MasterData from './pages/MasterData';
import MasterControlPanel from './pages/MasterControlPanel';
import SubscriptionManagement from './pages/SubscriptionManagement';
import Subscription from './pages/Subscription';
import Venues from './pages/Venues';
import Bookings from './pages/Bookings';
import Leads from './pages/Leads';
import CalendarView from './pages/CalendarView';
import MonthlyBookings from './pages/MonthlyBookings';
import MonthlyLeads from './pages/MonthlyLeads';
import Inventory from './pages/Inventory';
import Profile from './pages/Profile';
import Security from './pages/Security';
import BookingAnalytics from './pages/BookingAnalytics';
import FinancialAnalytics from './pages/FinancialAnalytics';
import BackupRestore from './pages/BackupRestore';
import StaffManagement from './pages/StaffManagement';
import ActivityLog from './pages/ActivityLog';
import Expenses from './pages/Expenses';
import Accounts from './pages/Accounts';
import Invoices from './pages/Invoices';
import Quotations from './pages/Quotations';
import YearView from './pages/YearView';
import Layout from './components/Layout';

import LoadingScreen from './components/LoadingScreen';

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { currentUser, loading } = useAuth();

  if (loading) {
    return <LoadingScreen />;
  }

  if (!currentUser) {
    return <Navigate to="/login" replace />;
  }

  return <Layout>{children}</Layout>;
};

function App() {
  const [showSplash, setShowSplash] = useState(() => {
    // Only show splash on first visit or if not shown in this session
    const hasSeenSplash = sessionStorage.getItem('hasSeenSplash');
    return !hasSeenSplash;
  });

  // Check version on app mount (silent check - no automatic popup)
  useEffect(() => {
    // Just check version silently - user can manually update from Settings
    checkVersion();
  }, []);

  const handleSplashComplete = useCallback(() => {
    sessionStorage.setItem('hasSeenSplash', 'true');
    setShowSplash(false);
  }, []);

  return (
    <AuthProvider>
      <SubscriptionProvider>
      <OfflineProvider>
      <div className="App">
        <OfflineBanner />
        {showSplash && <SplashScreen onComplete={handleSplashComplete} />}
        <BrowserRouter>
          <RenewalModal />
          <Routes>
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/choose-plan" element={<ChoosePlan />} />
            <Route path="/payment" element={<Payment />} />
            <Route path="/payment-success" element={<PaymentSuccess />} />
            <Route path="/payment-failure" element={<PaymentFailure />} />
            <Route path="/subscription-pending" element={<SubscriptionPending />} />
            <Route path="/subscription-expired" element={<SubscriptionExpired />} />
            <Route path="/subscription-rejected" element={<SubscriptionRejected />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/contact-us" element={<ContactUs />} />
            <Route path="/terms-and-conditions" element={<TermsAndConditions />} />
            <Route path="/" element={<LandingPage />} />
            <Route
              path="/dashboard"
              element={
                <ProtectedRoute>
                  <Dashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/master"
              element={
                <ProtectedRoute>
                  <MasterDashboard />
                </ProtectedRoute>
              }
            />
            <Route
              path="/master-data"
              element={
                <ProtectedRoute>
                  <MasterData />
                </ProtectedRoute>
              }
            />
            <Route
              path="/subscriptions"
              element={
                <ProtectedRoute>
                  <SubscriptionManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/master-control-panel"
              element={
                <ProtectedRoute>
                  <MasterControlPanel />
                </ProtectedRoute>
              }
            />
            <Route
              path="/subscription"
              element={
                <ProtectedRoute>
                  <Subscription />
                </ProtectedRoute>
              }
            />
            <Route
              path="/venues"
              element={
                <ProtectedRoute>
                  <Venues />
                </ProtectedRoute>
              }
            />
            <Route
              path="/bookings"
              element={
                <ProtectedRoute>
                  <Bookings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/leads"
              element={
                <ProtectedRoute>
                  <Leads />
                </ProtectedRoute>
              }
            />
            <Route
              path="/calendar"
              element={
                <ProtectedRoute>
                  <CalendarView />
                </ProtectedRoute>
              }
            />
            <Route
              path="/year-view"
              element={
                <ProtectedRoute>
                  <YearView />
                </ProtectedRoute>
              }
            />
            <Route
              path="/monthly-bookings"
              element={
                <ProtectedRoute>
                  <MonthlyBookings />
                </ProtectedRoute>
              }
            />
            <Route
              path="/monthly-leads"
              element={
                <ProtectedRoute>
                  <MonthlyLeads />
                </ProtectedRoute>
              }
            />
            <Route
              path="/inventory"
              element={
                <ProtectedRoute>
                  <Inventory />
                </ProtectedRoute>
              }
            />
            <Route
              path="/profile"
              element={
                <ProtectedRoute>
                  <Profile />
                </ProtectedRoute>
              }
            />
            <Route
              path="/security"
              element={
                <ProtectedRoute>
                  <Security />
                </ProtectedRoute>
              }
            />
            <Route
              path="/booking-analytics"
              element={
                <ProtectedRoute>
                  <BookingAnalytics />
                </ProtectedRoute>
              }
            />
            <Route
              path="/financial-analytics"
              element={
                <ProtectedRoute>
                  <FinancialAnalytics />
                </ProtectedRoute>
              }
            />
            <Route
              path="/backup-restore"
              element={
                <ProtectedRoute>
                  <BackupRestore />
                </ProtectedRoute>
              }
            />
            <Route
              path="/staff"
              element={
                <ProtectedRoute>
                  <StaffManagement />
                </ProtectedRoute>
              }
            />
            <Route
              path="/activity-log"
              element={
                <ProtectedRoute>
                  <ActivityLog />
                </ProtectedRoute>
              }
            />
            <Route
              path="/expenses"
              element={
                <ProtectedRoute>
                  <Expenses />
                </ProtectedRoute>
              }
            />
            <Route
              path="/accounts"
              element={
                <ProtectedRoute>
                  <Accounts />
                </ProtectedRoute>
              }
            />
            <Route
              path="/invoices"
              element={
                <ProtectedRoute>
                  <Invoices />
                </ProtectedRoute>
              }
            />
            <Route
              path="/quotations"
              element={
                <ProtectedRoute>
                  <Quotations />
                </ProtectedRoute>
              }
            />
          </Routes>
        </BrowserRouter>
        <Toaster />
      </div>
      </OfflineProvider>
      </SubscriptionProvider>
    </AuthProvider>
  );
}

export default App;
